/**
 * Global Logging Configuration
 *
 * Controls what gets logged throughout the EdgeStream pipeline.
 * Can be toggled at runtime by users.
 */

export interface LogSettingsConfig {
  hook?: {
    enabled?: boolean;
    logMessageBody?: boolean;
    logMetadata?: boolean;
    logAttributes?: boolean;
    logErrors?: boolean;
  };
  subscriber?: {
    enabled?: boolean;
    logDelivery?: boolean;
    logErrors?: boolean;
    logProcessingTime?: boolean;
  };
  persistence?: {
    enabled?: boolean;
    maxLogEntries?: number; // Max logs to keep in memory
    storageType?: 'memory' | 'indexeddb' | 'localstorage';
  };
}

/**
 * Global logging settings instance
 * Accessible throughout the application
 */
export class LogSettings {
  private static instance: LogSettings;

  // Hook logging
  private hookLoggingEnabled: boolean = true;
  private hookLogMessageBody: boolean = true;
  private hookLogMetadata: boolean = true;
  private hookLogAttributes: boolean = true;
  private hookLogErrors: boolean = true;

  // Subscriber logging
  private subscriberLoggingEnabled: boolean = true;
  private subscriberLogDelivery: boolean = true;
  private subscriberLogErrors: boolean = true;
  private subscriberLogProcessingTime: boolean = true;

  // Persistence
  private persistenceEnabled: boolean = true;
  private maxLogEntries: number = 1000;
  private storageType: 'memory' | 'indexeddb' | 'localstorage' = 'memory';

  private constructor() {}

  /**
   * Get or create singleton instance
   */
  static getInstance(): LogSettings {
    if (!LogSettings.instance) {
      LogSettings.instance = new LogSettings();
    }
    return LogSettings.instance;
  }

  /**
   * Initialize with configuration
   */
  static initialize(config: LogSettingsConfig): LogSettings {
    const instance = LogSettings.getInstance();
    instance.applyConfig(config);
    return instance;
  }

  /**
   * Apply configuration
   */
  applyConfig(config: LogSettingsConfig): void {
    if (config.hook) {
      this.hookLoggingEnabled = config.hook.enabled ?? this.hookLoggingEnabled;
      this.hookLogMessageBody = config.hook.logMessageBody ?? this.hookLogMessageBody;
      this.hookLogMetadata = config.hook.logMetadata ?? this.hookLogMetadata;
      this.hookLogAttributes = config.hook.logAttributes ?? this.hookLogAttributes;
      this.hookLogErrors = config.hook.logErrors ?? this.hookLogErrors;
    }

    if (config.subscriber) {
      this.subscriberLoggingEnabled = config.subscriber.enabled ?? this.subscriberLoggingEnabled;
      this.subscriberLogDelivery = config.subscriber.logDelivery ?? this.subscriberLogDelivery;
      this.subscriberLogErrors = config.subscriber.logErrors ?? this.subscriberLogErrors;
      this.subscriberLogProcessingTime = config.subscriber.logProcessingTime ?? this.subscriberLogProcessingTime;
    }

    if (config.persistence) {
      this.persistenceEnabled = config.persistence.enabled ?? this.persistenceEnabled;
      this.maxLogEntries = config.persistence.maxLogEntries ?? this.maxLogEntries;
      this.storageType = config.persistence.storageType ?? this.storageType;
    }
  }

  // ─── Hook Logging Settings ──────────────────────────────────────

  isHookLoggingEnabled(): boolean {
    return this.hookLoggingEnabled;
  }

  setHookLoggingEnabled(enabled: boolean): void {
    this.hookLoggingEnabled = enabled;
  }

  shouldLogHookMessageBody(): boolean {
    return this.hookLoggingEnabled && this.hookLogMessageBody;
  }

  setHookLogMessageBody(enabled: boolean): void {
    this.hookLogMessageBody = enabled;
  }

  shouldLogHookMetadata(): boolean {
    return this.hookLoggingEnabled && this.hookLogMetadata;
  }

  setHookLogMetadata(enabled: boolean): void {
    this.hookLogMetadata = enabled;
  }

  shouldLogHookAttributes(): boolean {
    return this.hookLoggingEnabled && this.hookLogAttributes;
  }

  setHookLogAttributes(enabled: boolean): void {
    this.hookLogAttributes = enabled;
  }

  shouldLogHookErrors(): boolean {
    return this.hookLoggingEnabled && this.hookLogErrors;
  }

  setHookLogErrors(enabled: boolean): void {
    this.hookLogErrors = enabled;
  }

  // ─── Subscriber Logging Settings ────────────────────────────────

  isSubscriberLoggingEnabled(): boolean {
    return this.subscriberLoggingEnabled;
  }

  setSubscriberLoggingEnabled(enabled: boolean): void {
    this.subscriberLoggingEnabled = enabled;
  }

  shouldLogSubscriberDelivery(): boolean {
    return this.subscriberLoggingEnabled && this.subscriberLogDelivery;
  }

  setSubscriberLogDelivery(enabled: boolean): void {
    this.subscriberLogDelivery = enabled;
  }

  shouldLogSubscriberErrors(): boolean {
    return this.subscriberLoggingEnabled && this.subscriberLogErrors;
  }

  setSubscriberLogErrors(enabled: boolean): void {
    this.subscriberLogErrors = enabled;
  }

  shouldLogSubscriberProcessingTime(): boolean {
    return this.subscriberLoggingEnabled && this.subscriberLogProcessingTime;
  }

  setSubscriberLogProcessingTime(enabled: boolean): void {
    this.subscriberLogProcessingTime = enabled;
  }

  // ─── Persistence Settings ──────────────────────────────────────

  isPersistenceEnabled(): boolean {
    return this.persistenceEnabled;
  }

  setPersistenceEnabled(enabled: boolean): void {
    this.persistenceEnabled = enabled;
  }

  getMaxLogEntries(): number {
    return this.maxLogEntries;
  }

  setMaxLogEntries(max: number): void {
    this.maxLogEntries = Math.max(100, max); // Minimum 100
  }

  getStorageType(): 'memory' | 'indexeddb' | 'localstorage' {
    return this.storageType;
  }

  setStorageType(type: 'memory' | 'indexeddb' | 'localstorage'): void {
    this.storageType = type;
  }

  /**
   * Get all current settings as object
   */
  getConfig(): LogSettingsConfig {
    return {
      hook: {
        enabled: this.hookLoggingEnabled,
        logMessageBody: this.hookLogMessageBody,
        logMetadata: this.hookLogMetadata,
        logAttributes: this.hookLogAttributes,
        logErrors: this.hookLogErrors,
      },
      subscriber: {
        enabled: this.subscriberLoggingEnabled,
        logDelivery: this.subscriberLogDelivery,
        logErrors: this.subscriberLogErrors,
        logProcessingTime: this.subscriberLogProcessingTime,
      },
      persistence: {
        enabled: this.persistenceEnabled,
        maxLogEntries: this.maxLogEntries,
        storageType: this.storageType,
      },
    };
  }

  /**
   * Reset to defaults
   */
  reset(): void {
    this.hookLoggingEnabled = true;
    this.hookLogMessageBody = true;
    this.hookLogMetadata = true;
    this.hookLogAttributes = false;
    this.hookLogErrors = true;

    this.subscriberLoggingEnabled = true;
    this.subscriberLogDelivery = true;
    this.subscriberLogErrors = true;
    this.subscriberLogProcessingTime = true;

    this.persistenceEnabled = true;
    this.maxLogEntries = 1000;
    this.storageType = 'memory';
  }
}

/**
 * Convenience function to access logging settings
 */
export function getLogSettings(): LogSettings {
  return LogSettings.getInstance();
}
